// client/src/components/admin/RoleChangeModal.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  Divider,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import Gateway from '../../api/gateway';
import { fetchAccess } from '../../store/thunks/authThunks';
import RoleBadge, { ROLE_OPTIONS } from './RoleBadge';

// ============================================================================
// Компонент
// ============================================================================

/**
 * Модал смены роли пользователя.
 *
 * @param {object} props
 * @param {object} props.user — объект из /api/access/users (kratos_identity_id, email, system_role, ...)
 * @param {() => void} props.onClose
 * @param {() => void} [props.onSuccess] — вызывается после успешной смены (обновить таблицу)
 */
export default function RoleChangeModal({ user, onClose, onSuccess }) {
  const dispatch = useDispatch();

  const initialRole = user?.system_role || 'user';

  const [role, setRole] = useState(initialRole);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.kratos_identity_id) {
      setError('Не удалось определить kratos_identity_id пользователя');
      return;
    }
    if (role === initialRole) {
      setError('Роль не изменилась');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await Gateway.api.accessAdmin.changeUserRole(
        user.kratos_identity_id,
        role,
        reason.trim() || null
      );

      // Обновляем access-профиль текущего пользователя (на случай, если поменяли себя)
      try {
        await dispatch(fetchAccess()).unwrap();
      } catch (refreshErr) {
        console.warn('⚠️ Не удалось обновить access после смены роли:', refreshErr);
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      const status = err.response?.status;
      const apiError = err.response?.data?.error;
      if (status === 403) {
        setError(apiError || 'Недостаточно прав для смены роли');
      } else if (status === 404) {
        setError('Пользователь не найден');
      } else if (status === 400) {
        setError(apiError || 'Некорректные данные');
      } else {
        setError(apiError || err.message || 'Не удалось сменить роль');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const displayName = user?.display_name || user?.email || user?.kratos_identity_id || 'Пользователь';
  const roleChanged = role !== initialRole;

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h6" component="span">
              Сменить роль
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              • ID: {user?.kratos_identity_id?.substring(0, 8)}…
            </Typography>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent>
          <Box sx={{ mb: 2, mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Текущая роль:
            </Typography>
            <RoleBadge role={initialRole} />
          </Box>

          <TextField
            select
            fullWidth
            label="Новая роль"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            disabled={loading}
            margin="normal"
            helperText={roleChanged ? '' : 'Выберите другую роль'}
          >
            {ROLE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RoleBadge role={opt.value} size="small" showTooltip={false} />
                </Box>
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            label="Причина (необязательно)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={loading}
            margin="normal"
            placeholder="Например: повышение до администратора по результатам работы"
            helperText="Причина попадёт в audit-лог"
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {roleChanged && !error && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Роль будет изменена с <RoleBadge role={initialRole} /> на{' '}
              <RoleBadge role={role} />.
            </Alert>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !roleChanged}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? 'Сохранение…' : 'Сменить роль'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}